import { useState } from 'react'

const BASE_URL = import.meta.env.VITE_API_BASE_URL as string

function StarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0)
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="text-2xl transition-colors duration-150"
          style={{ color: star <= (hovered || value) ? '#2d5a3d' : '#d1ccc4' }}
        >
          ★
        </button>
      ))}
    </div>
  )
}

export default function LeaveReview() {
  const [form, setForm] = useState({ guest_name: '', city: '', quote: '', rating: 5 })
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${BASE_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      const data = await res.json() as { success: boolean; error?: string }
      if (data.success) {
        setSubmitted(true)
      } else {
        setError(data.error ?? 'Something went wrong. Please try again.')
      }
    } catch {
      setError('Could not submit. Please check your connection.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-cream-100">

      {/* Hero */}
      <div className="relative h-56 overflow-hidden">
        <img src="/images/optimized/IMG_5169.webp" fetchPriority="high"
          className="h-full w-full object-cover" alt="Scenic Solitude" />
        <div className="absolute inset-0 bg-forest-950/60" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <p className="text-[10px] tracking-[0.35em] uppercase text-white/55 font-semibold mb-3">Thank You for Staying</p>
          <h1 className="text-3xl font-semibold text-white tracking-tight">Share Your Experience</h1>
        </div>
      </div>

      {/* Form */}
      <div className="mx-auto max-w-xl px-8 py-16">

        {submitted ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">🌿</div>
            <h2 className="text-2xl font-semibold text-ink mb-3">Thank you!</h2>
            <p className="text-sm text-ink-soft font-light leading-relaxed">
              Your review has been submitted and will appear on our website once reviewed. We truly appreciate your kind words.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-ink-muted font-semibold mb-6">
                Your stay at Scenic Solitude
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-muted mb-2">Your Name *</label>
                <input
                  type="text"
                  required
                  value={form.guest_name}
                  onChange={e => setForm(f => ({ ...f, guest_name: e.target.value }))}
                  className="w-full border border-cream-300 bg-cream-50 px-4 py-3 text-sm text-ink placeholder:text-ink-light focus:outline-none focus:border-ink/40 transition-colors"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-muted mb-2">City *</label>
                <input
                  type="text"
                  required
                  value={form.city}
                  onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                  className="w-full border border-cream-300 bg-cream-50 px-4 py-3 text-sm text-ink placeholder:text-ink-light focus:outline-none focus:border-ink/40 transition-colors"
                  placeholder="Where are you from?"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-muted mb-2">Your Rating *</label>
              <StarRating value={form.rating} onChange={v => setForm(f => ({ ...f, rating: v }))} />
            </div>

            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-muted mb-2">Your Review *</label>
              <textarea
                required
                rows={5}
                value={form.quote}
                onChange={e => setForm(f => ({ ...f, quote: e.target.value }))}
                className="w-full border border-cream-300 bg-cream-50 px-4 py-3 text-sm text-ink placeholder:text-ink-light focus:outline-none focus:border-ink/40 transition-colors resize-none"
                placeholder="Tell us about your stay…"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 px-4 py-3">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full border border-ink px-6 py-3.5 text-[11px] tracking-widest2 uppercase font-medium text-ink hover:bg-ink hover:text-cream-50 transition-all duration-200 disabled:opacity-50"
            >
              {loading ? 'Submitting…' : 'Submit Review'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
